import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Play, TrendingUp } from 'lucide-react';

/**
 * StudentIncomeProof — horizontal video carousel of AlveeFx student income proofs.
 * • First video autoplays MUTED (only reliable way across Chrome / Safari / iOS).
 * • On the first user gesture anywhere on the page, we unmute + turn volume up
 *   via YouTube IFrame postMessage API (no extra script needed).
 * • Left / right arrows + swipe. Neighbors peek on desktop, single-per-view mobile.
 * • Clicking a neighboring card scrolls it into center.
 */

// 9 YouTube IDs, in order.
const STUDENT_VIDEOS = [
  { id: 'cZzu12FbCJg' },
  { id: 'WNLpod8934o' },
  { id: 'WSXF5bO19ww' },
  { id: '0RIVj06TrdQ' },
  { id: 'KzsFNy4z7eA' },
  { id: 'd1lTH3qIeEY' },
  { id: 'ZJ8_Wg0kLyM' },
  { id: 'wv5sSqWxY8g' },
  { id: 'uiW-at3FtX4' },
];

function ytPostMessage(iframeEl, func, args = []) {
  if (!iframeEl || !iframeEl.contentWindow) return;
  try {
    iframeEl.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      '*'
    );
  } catch {
    /* ignore */
  }
}

export default function StudentIncomeProof({ isBangla = false }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    dragFree: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const iframeRefs = useRef([]);

  const T = useMemo(
    () =>
      isBangla
        ? {
            kicker: 'রিয়েল ইনকাম প্রুফ',
            title: 'আমাদের স্টুডেন্টদের ইনকাম',
            sub: 'AlveeFx কমিউনিটি থেকে যারা রিয়েল প্রফিট করেছে — তাদের নিজেদের ভিডিও প্রুফ।',
            unmute: 'সাউন্ড অন করতে ট্যাপ করুন',
            muted: 'সাউন্ড অন',
            prev: 'আগের ভিডিও',
            next: 'পরের ভিডিও',
          }
        : {
            kicker: 'REAL INCOME PROOF',
            title: 'AlveeFx Student Earnings',
            sub: 'Watch real students share their journey and profits from the AlveeFx community.',
            unmute: 'Tap to unmute',
            muted: 'Sound on',
            prev: 'Previous video',
            next: 'Next video',
          },
    [isBangla]
  );

  // Track active slide.
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  // Pause non-active videos, ensure active plays.
  useEffect(() => {
    iframeRefs.current.forEach((el, i) => {
      if (!el) return;
      if (i === selectedIndex) {
        ytPostMessage(el, 'playVideo');
      } else {
        ytPostMessage(el, 'pauseVideo');
      }
    });
  }, [selectedIndex]);

  // Unmute the active iframe on first user gesture anywhere.
  useEffect(() => {
    let done = false;
    const unmuteNow = () => {
      if (done) return;
      done = true;
      const el = iframeRefs.current[selectedIndex];
      if (el) {
        ytPostMessage(el, 'unMute');
        ytPostMessage(el, 'setVolume', [100]);
        ytPostMessage(el, 'playVideo');
      }
      setMuted(false);
      cleanup();
    };
    const opts = { once: true, passive: true, capture: true };
    const cleanup = () => {
      window.removeEventListener('pointerdown', unmuteNow, true);
      window.removeEventListener('touchstart', unmuteNow, true);
      window.removeEventListener('keydown', unmuteNow, true);
      window.removeEventListener('scroll', unmuteNow, true);
    };
    window.addEventListener('pointerdown', unmuteNow, opts);
    window.addEventListener('touchstart', unmuteNow, opts);
    window.addEventListener('keydown', unmuteNow, opts);
    window.addEventListener('scroll', unmuteNow, opts);
    return cleanup;
  }, [selectedIndex]);

  const toggleMute = useCallback(() => {
    const el = iframeRefs.current[selectedIndex];
    if (!el) return;
    if (muted) {
      ytPostMessage(el, 'unMute');
      ytPostMessage(el, 'setVolume', [100]);
      ytPostMessage(el, 'playVideo');
      setMuted(false);
    } else {
      ytPostMessage(el, 'mute');
      setMuted(true);
    }
  }, [muted, selectedIndex]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section className="relative z-10 pb-10 pt-2" data-testid="student-income-proof">
      <div className="container-x max-w-6xl mx-auto px-2">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.08)] px-3.5 py-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-[hsl(43_92%_56%)]" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] font-semibold">
              {T.kicker}
            </span>
          </div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl text-white">
            {T.title}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
            {T.sub}
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-3 sm:gap-4">
              {STUDENT_VIDEOS.map((v, i) => {
                const isActive = i === selectedIndex;
                // Autoplay + mute on first slide; enablejsapi + origin for postMessage control.
                const origin = typeof window !== 'undefined' ? window.location.origin : '';
                const params = new URLSearchParams({
                  autoplay: i === 0 ? '1' : '0',
                  mute: '1', // Required for cross-browser autoplay.
                  enablejsapi: '1',
                  origin,
                  playsinline: '1',
                  rel: '0',
                  modestbranding: '1',
                  controls: '1',
                });
                const src = `https://www.youtube.com/embed/${v.id}?${params.toString()}`;
                return (
                  <div
                    key={v.id}
                    className={
                      'shrink-0 basis-[86%] sm:basis-[62%] md:basis-[48%] lg:basis-[38%] ' +
                      'transition-all duration-300 ease-out ' +
                      (isActive ? 'opacity-100 scale-100' : 'opacity-55 scale-[0.94]')
                    }
                    onClick={() => {
                      if (!isActive) emblaApi?.scrollTo(i);
                    }}
                    role="button"
                    tabIndex={0}
                    data-testid={`student-video-slide-${i}`}
                    data-active={isActive ? 'true' : 'false'}
                  >
                    <div
                      className={
                        'relative aspect-[9/16] rounded-2xl overflow-hidden ' +
                        'border ' +
                        (isActive
                          ? 'border-[hsl(43_92%_56%)]/60 shadow-[0_18px_44px_-14px_rgba(245,185,66,0.35)]'
                          : 'border-white/10') +
                        ' bg-black'
                      }
                    >
                      <iframe
                        ref={(el) => (iframeRefs.current[i] = el)}
                        title={`AlveeFx student proof ${i + 1}`}
                        src={src}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                      />
                      {!isActive && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/45 backdrop-blur-[1px] pointer-events-none">
                          <div className="rounded-full bg-[hsl(43_92%_56%)]/95 p-3 shadow-lg">
                            <Play className="h-5 w-5 text-black fill-black" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Arrows */}
          <button
            type="button"
            aria-label={T.prev}
            onClick={scrollPrev}
            data-testid="student-video-prev"
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/70 backdrop-blur border border-white/15 text-white hover:bg-black/90 hover:border-[hsl(43_92%_56%)]/60 transition-colors flex items-center justify-center shadow-lg"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label={T.next}
            onClick={scrollNext}
            data-testid="student-video-next"
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/70 backdrop-blur border border-white/15 text-white hover:bg-black/90 hover:border-[hsl(43_92%_56%)]/60 transition-colors flex items-center justify-center shadow-lg"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Unmute pill (only shows while muted) */}
          {muted && (
            <button
              type="button"
              onClick={toggleMute}
              data-testid="student-video-unmute"
              className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 rounded-full bg-[hsl(43_92%_56%)] text-black px-4 py-2 text-xs font-bold shadow-[0_10px_30px_-8px_rgba(245,185,66,0.55)] hover:bg-[hsl(43_92%_62%)] transition-colors animate-pulse"
            >
              <VolumeX className="h-3.5 w-3.5" />
              {T.unmute}
            </button>
          )}
          {!muted && (
            <button
              type="button"
              onClick={toggleMute}
              data-testid="student-video-mute"
              className="absolute top-3 right-14 z-10 h-9 w-9 rounded-full bg-black/70 border border-white/15 text-white hover:bg-black/90 flex items-center justify-center"
              aria-label={T.muted}
            >
              <Volume2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Dots */}
        <div className="mt-4 flex items-center justify-center gap-1.5">
          {STUDENT_VIDEOS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to video ${i + 1}`}
              className={
                'h-1.5 rounded-full transition-all duration-300 ' +
                (i === selectedIndex
                  ? 'w-6 bg-[hsl(43_92%_56%)]'
                  : 'w-1.5 bg-white/25 hover:bg-white/45')
              }
              data-testid={`student-video-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
